import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, MessageCircle } from "lucide-react";
import { waLink } from "../config/business";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "./shared";

// Vídeo promocional: silenciado y en bucle automático; el visitante puede activar el sonido
const PromoVideo = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  // Reproduce solo cuando está en pantalla (ahorra datos/batería en móvil) y fuerza el autoplay silenciado
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <section data-testid="promo-video-section" className="section-alt">
      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 sm:px-8 py-16 sm:py-24 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="mb-5 h-1 w-14 rounded-full bg-gradient-to-r from-yellow-300 via-amber-400 to-amber-600 shadow-[0_0_18px_rgba(234,179,8,0.45)]" />
          <h2 data-testid="promo-video-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-[-0.03em] leading-[1.08] text-slate-50">
            {t.video.title}
          </h2>
          <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400">{t.video.sub}</p>
          <a
            data-testid="promo-video-whatsapp-cta"
            href={waLink(t.hero.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-wa mt-8 rounded-2xl px-7 py-4 text-base"
          >
            <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
            {t.hero.whatsapp}
          </a>
        </Reveal>

        <Reveal delay={0.15} className="justify-self-center lg:justify-self-end">
          <div className="relative w-[min(78vw,300px)] sm:w-[320px]">
            <div className="pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-amber-400/10 blur-3xl" />
            <div className="card-elevated relative overflow-hidden rounded-[2rem] border-white/15 p-2">
              <video
                ref={ref}
                data-testid="promo-video"
                className="aspect-[9/16] w-full rounded-[1.6rem] bg-[#0B1526] object-cover"
                src="/videos/promo.mp4"
                poster="/videos/promo-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={t.video.aria}
              />
              <button
                type="button"
                data-testid="promo-video-sound-toggle"
                onClick={toggleSound}
                aria-pressed={!muted}
                className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-[#0A1526]/80 px-3.5 py-2 text-xs font-semibold text-slate-100 shadow-lg backdrop-blur-md transition-colors hover:bg-[#0A1526]"
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4 text-amber-400" />}
                {muted ? t.video.unmute : t.video.mute}
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default PromoVideo;
